import React, { useContext, useEffect, useState } from 'react';
import { Row, Col, Button, Card } from 'react-bootstrap';
import Select from 'react-select';
import { useTranslation } from 'react-i18next';
import { SubscriptionService } from './service';
import { ErrorToast, SuccessToast } from '../../shared/toasters/toasters';
import BasicModal from '../../Common/BasicModal';
import { EnumsContext } from '../../context/EnumsContext';

interface SubscriptionsTabProps {
  companyId: number;
}

const SubscriptionsTab: React.FC<SubscriptionsTabProps> = ({ companyId }) => {
  const { t } = useTranslation();
  const [subscriptions, setSubscriptions] = useState<any[]>([]);
  const [currentSubscription, setCurrentSubscription] = useState<any>(null);
  const [subscriptionHistory, setSubscriptionHistory] = useState<any[]>([]);
  const [packages, setPackages] = useState<any[]>([]);
  const [selectedPackage, setSelectedPackage] = useState<number | null>(null);
  const [duration, setDuration] = useState<string>('month');
  const [showModal, setShowModal] = useState(false);
  const { enums } = useContext(EnumsContext);
  const subscriptionDuration = enums.subscriptionDuration;

  const fetchSubscriptions = async () => {
    try {
      const response = await SubscriptionService.getCompanyCurrent(companyId);
      if (response.subscription) {
        setCurrentSubscription(response.subscription);
        setSubscriptions([response.subscription]);
      } else {
        setCurrentSubscription(null);
        setSubscriptions([]);
      }
    } catch (error) {
      ErrorToast(error);
    }
  };

  const fetchSubscriptionHistory = async () => {
    try {
      const response = await SubscriptionService.getCompanySubscriptionHistory(companyId);
      setSubscriptionHistory(response);
    } catch (error) {
      ErrorToast(error);
    }
  };

  const fetchPackages = async () => {
    try {
      const response = await SubscriptionService.getPackages({type: 'company', perPage: 0, page: 1});
      const filteredPackages = response.data
        .filter((pkg: any) => pkg.isActive)
        .map((pkg: any) => ({
          label: pkg.name,
          value: pkg.id,
          type: pkg.type,
        }));
      setPackages(filteredPackages);
    } catch (error) {
      ErrorToast(error);
    }
  };

  const fetchAllData = async () => {
    await Promise.all([fetchSubscriptions(), fetchSubscriptionHistory(), fetchPackages()]);
  };

  useEffect(() => {
    fetchAllData();
  }, [companyId]);

  const assignPackage = async () => {
    try {
      await SubscriptionService.assignCompanyPackage({
        company: companyId,
        package: selectedPackage!,
        duration,
      });
      SuccessToast(t('company.packageAssigned'));
      setSelectedPackage(null);
      setDuration('month');
      setShowModal(false);
      await fetchAllData();
    } catch (error) {
      ErrorToast(error);
    }
  };

  if (!subscriptionHistory.length && !currentSubscription && !packages.length) {
    return <div>{t('common.loading')}</div>;
  }

  return (
    <Row>
      {/* Current Subscription */}
      <Col sm={3}>
        <Card className="shadow-none border my-4">
          <Card.Body>
            <h6 className="mb-3 fw-medium">{t('company.currentSubscription')}</h6>
            {currentSubscription ? (
              <>
                <h4 className="mb-3 fw-normal text-muted">
                  {currentSubscription.package.name}
                </h4>
                <p className="mb-1">
                  <strong>{t('company.startDate')}:</strong> {currentSubscription.startDate}
                </p>
                <p className="mb-1">
                  <strong>{t('company.endDate')}:</strong> {currentSubscription.endDate}
                </p>
                <p className="mb-0">
                  <strong>{t('company.duration')}:</strong> {t(`enums.subscriptionDuration.${currentSubscription.duration}`)}
                </p>
              </>
            ) : (
              <p className="text-muted mb-0">{t('company.noCurrentSubscription')}</p>
            )}
          </Card.Body>
        </Card>
      </Col>

      {/* Assign New Package Button */}
      <Col sm={12} className="text-end">
        <Button variant="primary" onClick={() => setShowModal(true)}>
          {t('company.assignPackage')}
        </Button>
      </Col>

      {/* Subscription History */}
      <Col sm={12} className="mt-4">
        <h4>{t('company.subscriptionHistory')}</h4>
        {subscriptionHistory.length > 0 ? (
          <table className="table">
            <thead>
              <tr>
                <th>{t('company.packageName')}</th>
                <th>{t('company.startDate')}</th>
                <th>{t('company.endDate')}</th>
                <th>{t('company.duration')}</th>
                <th>{t('company.isActive')}</th>
              </tr>
            </thead>
            <tbody>
              {subscriptionHistory.map((sub, index) => (
                <tr key={index}>
                  <td>{sub.package.name}</td>
                  <td>{sub.startDate}</td>
                  <td>{sub.endDate}</td>
                  <td>{t(`enums.subscriptionDuration.${sub.duration}`)}</td>
                  <td>{sub.isActive ? t('common.yes') : t('common.no')}</td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <p>{t('company.noSubscriptionHistory')}</p>
        )}
      </Col>

      {/* Assign Package Modal */}
      <BasicModal
        show={showModal}
        handleClose={() => setShowModal(false)}
        handleSave={assignPackage}
        title={t('company.assignNewPackage')}
        disabled={!selectedPackage || !duration}
      >
        <div className="mb-3">
          <label className="form-label">{t('company.package')}</label>
          <Select
            id="package"
            classNamePrefix="react-select"
            value={packages.find((pkg) => pkg.value === selectedPackage)}
            onChange={(selectedOption) => setSelectedPackage(selectedOption ? selectedOption.value : null)}
            options={packages.filter((pkg) => pkg.type === 'company')}
            isClearable
          />
        </div>
        <div className="mb-3">
          <label className="form-label">{t('company.duration')}</label>
          <Select
            id="duration"
            classNamePrefix="react-select"
            value={{ label: t(`enums.subscriptionDuration.${duration}`), value: duration }}
            onChange={(selectedOption) => setDuration(selectedOption ? selectedOption.value : 'month')}
            options={subscriptionDuration.map((d: string) => ({
              label: t(`enums.subscriptionDuration.${d}`),
              value: d,
            }))}
          />
        </div>
      </BasicModal>
    </Row>
  );
};

export default SubscriptionsTab;